// Ring buffer of recent console.{log,info,warn,error} entries. Installed once
// at app boot — the Report Issue modal reads .snapshot() to attach the last
// N entries to a submitted ticket.

export interface ConsoleEntry {
  level: 'log' | 'info' | 'warn' | 'error';
  message: string;
  stack?: string;
  timestamp: number;
}

const MAX_ENTRIES = 50;
let buffer: ConsoleEntry[] = [];
let installed = false;

function stringifyArg(arg: any): string {
  if (arg === null) return 'null';
  if (arg === undefined) return 'undefined';
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === 'object') {
    try {
      return JSON.stringify(arg).slice(0, 500);
    } catch {
      return '[unstringifiable object]';
    }
  }
  return String(arg).slice(0, 500);
}

function push(entry: ConsoleEntry): void {
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) {
    buffer = buffer.slice(-MAX_ENTRIES);
  }
}

function wrap(level: ConsoleEntry['level'], original: (...args: any[]) => void) {
  return (...args: any[]) => {
    try {
      const messageParts = args.map(stringifyArg);
      const errArg = args.find((a) => a instanceof Error);
      push({
        level,
        message: messageParts.join(' ').slice(0, 1000),
        stack: errArg?.stack,
        timestamp: Date.now(),
      });
    } catch {
      // Don't let logging fail.
    }
    return original(...args);
  };
}

export function installConsoleBuffer(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  console.log = wrap('log', console.log.bind(console));
  console.info = wrap('info', console.info.bind(console));
  console.warn = wrap('warn', console.warn.bind(console));
  console.error = wrap('error', console.error.bind(console));

  window.addEventListener('error', (e) => {
    push({
      level: 'error',
      message: `Uncaught: ${e.message}`,
      stack: e.error?.stack,
      timestamp: Date.now(),
    });
  });
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    push({
      level: 'error',
      message: `Unhandled rejection: ${stringifyArg(e.reason)}`,
      stack: e.reason?.stack,
      timestamp: Date.now(),
    });
  });
}

export function snapshotConsole(maxLast = 25): ConsoleEntry[] {
  return buffer.slice(-maxLast);
}

export function clearConsoleBuffer(): void {
  buffer = [];
}
